import React, { useState } from 'react';
import {
  duplicateQuoteRequest,
  setQuoteRequestInProgress,
  completeQuoteRequest,
  cancelQuoteRequest,
} from '../api';
import './QuoteActions.css';

/**
 * QuoteActions Component
 * Provides action buttons for quote request state management
 *
 * @param {Object} props - Component props
 * @param {Object} props.quoteRequest - The quote request object
 * @param {Function} props.onActionComplete - Callback when action completes successfully
 * @param {string} [props.className] - Additional CSS classes
 */
const QuoteActions = ({ quoteRequest, onActionComplete, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [revisionReason, setRevisionReason] = useState('better_offer');

  const handleAction = async (actionFn, actionName) => {
    if (!window.confirm(`Are you sure you want to ${actionName} this quote request?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await actionFn(quoteRequest.id);
      if (onActionComplete) {
        onActionComplete(response.data, actionName);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Action failed';
      setError(errorMsg);
      console.error(`Failed to ${actionName}:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateClick = () => {
    setShowDuplicateModal(true);
  };

  const handleDuplicateConfirm = async () => {
    setLoading(true);
    setError(null);
    setShowDuplicateModal(false);

    try {
      const response = await duplicateQuoteRequest(quoteRequest.id, {
        set_in_progress: true,
        extend_deadline_days: 2,
        revision_reason: revisionReason
      });
      if (onActionComplete) {
        onActionComplete(response.data, 'duplicate');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Duplicate failed';
      setError(errorMsg);
      console.error('Failed to duplicate:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateModal(false);
    setRevisionReason('better_offer'); // Reset to default
  };

  const handleSetInProgress = () => {
    handleAction(setQuoteRequestInProgress, 'set to in progress');
  };

  const handleComplete = () => {
    handleAction(completeQuoteRequest, 'complete');
  };

  const handleCancel = () => {
    handleAction(cancelQuoteRequest, 'cancel');
  };

  const currentState = quoteRequest?.state || 'draft';

  return (
    <div className={`quote-actions ${className}`}>
      {error && (
        <div className="quote-actions-error">
          {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="quote-actions-buttons">
        {/* Duplicate & Set In Progress - Available for all states */}
        <button
          onClick={handleDuplicateClick}
          disabled={loading}
          className="action-button duplicate-button"
          title="Duplicate this quote request and set to in progress"
        >
          📋 Duplicate & Set In Progress
        </button>

        {/* Set In Progress - Only for draft or assigned */}
        {(currentState === 'draft' || currentState === 'assigned') && (
          <button
            onClick={handleSetInProgress}
            disabled={loading}
            className="action-button in-progress-button"
            title="Set quote request to in progress"
          >
            ▶️ Set In Progress
          </button>
        )}

        {/* Complete - Only for in_progress */}
        {currentState === 'in_progress' && (
          <button
            onClick={handleComplete}
            disabled={loading}
            className="action-button complete-button"
            title="Mark quote request as completed"
          >
            ✅ Complete
          </button>
        )}

        {/* Cancel - Available for draft, assigned, in_progress */}
        {(currentState === 'draft' || currentState === 'assigned' || currentState === 'in_progress') && (
          <button
            onClick={handleCancel}
            disabled={loading}
            className="action-button cancel-button"
            title="Cancel this quote request"
          >
            ❌ Cancel
          </button>
        )}

        {loading && <span className="loading-spinner">⏳ Processing...</span>}
      </div>

      {/* Duplication Reason Modal */}
      {showDuplicateModal && (
        <div className="modal-overlay" onClick={handleDuplicateCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Quote Request</h3>
              <button onClick={handleDuplicateCancel} className="modal-close">×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Select a reason for duplicating this quote request:
              </p>
              <div className="revision-reason-options">
                <label className="reason-option">
                  <input
                    type="radio"
                    name="revision_reason"
                    value="better_offer"
                    checked={revisionReason === 'better_offer'}
                    onChange={(e) => setRevisionReason(e.target.value)}
                  />
                  <div className="reason-details">
                    <span className="reason-title">💰 Better Offer</span>
                    <span className="reason-description">Submit an improved or alternative offer</span>
                  </div>
                </label>
                <label className="reason-option">
                  <input
                    type="radio"
                    name="revision_reason"
                    value="mistake"
                    checked={revisionReason === 'mistake'}
                    onChange={(e) => setRevisionReason(e.target.value)}
                  />
                  <div className="reason-details">
                    <span className="reason-title">🔧 Mistake</span>
                    <span className="reason-description">Correct errors or pricing mistakes</span>
                  </div>
                </label>
                <label className="reason-option">
                  <input
                    type="radio"
                    name="revision_reason"
                    value="amendment"
                    checked={revisionReason === 'amendment'}
                    onChange={(e) => setRevisionReason(e.target.value)}
                  />
                  <div className="reason-details">
                    <span className="reason-title">📝 Amendment</span>
                    <span className="reason-description">Make changes to the existing quote</span>
                  </div>
                </label>
              </div>
              <div className="modal-info">
                ℹ️ The duplicated quote will be set to <strong>in progress</strong> and deadline extended by <strong>2 days</strong>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleDuplicateCancel} className="modal-button cancel">
                Cancel
              </button>
              <button onClick={handleDuplicateConfirm} className="modal-button confirm">
                Duplicate Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteActions;
